import { useEffect, useState, ElementType } from 'react';
import { useAuth } from '../auth/AuthContext';
import { getContent, saveContent } from '../lib/api';
import './EditableText.css';

type Props = {
  id: string;
  defaultValue: string;
  as?: ElementType;
  className?: string;
};

export function EditableText({ id, defaultValue, as: Tag = 'p', className }: Props) {
  const { isAuthenticated, idToken } = useAuth();
  const [value, setValue] = useState(defaultValue);
  const [exists, setExists] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(defaultValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    getContent(id)
      .then((content) => {
        if (cancelled || content === null) return;
        setValue(content);
        setDraft(content);
        setExists(true);
      })
      .catch(() => {
        // Keep the hardcoded defaultValue if the API isn't reachable/configured yet.
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleSave = async () => {
    if (!idToken) return;
    setSaving(true);
    setError(null);
    try {
      await saveContent(id, draft, idToken, exists);
      setValue(draft);
      setExists(true);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className={`editable-text-editor ${className ?? ''}`}>
        {error && <p className="editable-text-error">{error}</p>}
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} />
        <div className="editable-text-actions">
          <button type="button" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => {
              setDraft(value);
              setEditing(false);
              setError(null);
            }}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <Tag className={`editable-text-content ${className ?? ''}`}>
      {value}
      {isAuthenticated && (
        <button
          type="button"
          className="editable-text-edit-btn"
          onClick={() => setEditing(true)}
          aria-label={`Edit ${id}`}
        >
          ✎
        </button>
      )}
    </Tag>
  );
}
